import { EnrichmentListJourneyStore } from "../../../services/state/enrichment-list-journey.store";
import { CommonModule, Location } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  linkedSignal,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
} from "@angular/core";
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
} from "@angular/router";
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  of,
  skip,
  Subscription,
  switchMap,
  tap,
} from "rxjs";
import { RoleService } from "@app/shared/services/role.service";
import {
  ApiLanguage,
  BreadcrumbService,
  LanguageConfigStore,
  LanguageModeService,
  LanguageModeValue,
  LanguageService,
  OverlayService,
} from "@app/shared";
import {
  IProductOffering,
  IProductOfferingPrice,
  IProductSpecification,
} from "@app/shared/models/product-offering.model";
import {
  isClosedStatus,
  isRejectableStatus,
} from "@app/shared/models/enrichment-list.model";
import { packageStatusView } from "@app/shared/models/package-status.model";
import { mayKeepLiveOnSave } from "../../../utils/price-authority.util";
import {
  lifecycleActions,
  nextToPublish,
} from "../../../utils/price-lifecycle.util";
import { EnrichmentService } from "@app/shared/services/enrichment.service";
import { formatScheduledAt } from "@app/shared/utils/publish-schedule.util";
import { OmniarkToastService } from "@app/shared/services/toast.service";
import { Permissions } from "@core/models/permissions.generated";
import { AuthService } from "@core/services/auth.service";
import { PermissionService } from "@core/services/permission.service";
import { packageOwner } from "@app/feature/management/enrichment-mode/online-store-owner";
import { packageEditorLink } from "@app/feature/management/enrichment-mode/package-editor-url";
import { FeatureFlagService } from "@core/services/feature-flag.service";
import { logger } from "@core/utils/logger";
import {
  ButtonComponent,
  OmniarkUiModule,
  RadioComponent,
  StatusComponent,
  TabItem,
} from "@corp-ais/omniark-ui";
import { DateTimePickerComponent } from "@corp-ais/omniark-ui/date-time-picker";
import { APP_ROUTES } from "@shared/constants/app-routes.constants";
import { OmniarkModalService } from "../../../../../../shared/services/modal.service";
import { isExactMyAisChannel } from "@app/shared/constants/channel.constants";
import {
  buildSaveConfirmCopy,
  DEFAULT_PRICE_SAVE_DECISION,
  PriceSaveDecision,
  SaveConfirmCopy,
  saveSuccessToast,
} from "./save-confirm.util";
import {
  detailToModel,
  isFlatEnrichmentModel,
} from "@app/shared/mappers/enrichment-detail.adapter";
import { AgreementContentStore } from "../../../services/agreement-content.store";
import { ChannelLinkService } from "../../../services/channel-link/channel-link.service";
import { EnrichmentChannelDirectory } from "../../../services/enrichment-channel-directory.service";
import {
  ENRICHMENT_HEADER_TRANSPORT,
  EnrichmentDataService,
  PublishIssue,
  PublishReadiness,
} from "../../../services/enrichment-data.service";
import {
  PREVIEW_RAIL_BREAKPOINT_PX,
  defaultPreviewOpen,
  loadPreviewPref,
  savePreviewPref,
} from "../../../services/state/preview-pref";
import { IssueRef } from "../../../services/publish/tab-readiness.types";
import {
  EnrichmentEditPackageHero,
  HeroPackage,
} from "../enrichment-edit-package-hero/enrichment-edit-package-hero";
import { EnrichmentEditPackageStepper } from "../enrichment-edit-package-stepper/enrichment-edit-package-stepper";
import { EnrichmentEditPackageTour } from "../enrichment-edit-package-tour/enrichment-edit-package-tour";
import { EnrichmentPreviewPanel } from "../enrichment-preview-panel/enrichment-preview-panel";
import { EnrichmentPricing } from "../enrichment-pricing/enrichment-pricing";
import { EnrichmentShelf } from "../enrichment-shelf/enrichment-shelf";
import { EnrichmentSpecialPromotion } from "../enrichment-special-promotion/enrichment-special-promotion";
import { EnrichmentPackageSettings } from "../enrichment-package-settings/enrichment-package-settings";
import { TabReadinessBanner } from "../tab-readiness-banner/tab-readiness-banner";
import { EnrichmentDirtyTracker } from "./enrichment-dirty-tracker";
import { EnrichmentPriceDirtyStore } from "./enrichment-price-dirty.store";
import { EnrichmentPriceLockStore } from "./enrichment-price-lock.store";
import { EnrichmentIsSofEditStore } from "./enrichment-is-sof-edit.store";
import { EnrichmentDisplayQuantityLockStore } from "./enrichment-display-quantity-lock.store";
import {
  EnrichmentSaveCoordinator,
  NonEmptyPriceIds,
} from "./enrichment-save-coordinator";
import { MissingField } from "./flat-language-coverage.util";
import { triggerPublishConfetti } from "./publish-confetti.util";
import { PublishDrawerController } from "./publish-drawer.controller";
import { ActivePriceReadiness } from "./active-price-readiness";
import { ActivePriceStore } from "./active-price.store";
import { SectionDirtyStore } from "./section-dirty.store";
import { EnrichmentPricingGroupStore } from "../enrichment-pricing/enrichment-pricing-group.store";
import { sectionForTarget } from "../enrichment-pricing/section-target.util";
import { SectionSaveBar } from "./section-save-bar";
import { advanceSnapshot } from "./snapshot-merge.util";
import {
  LanguageSetStatus,
  languageSetRefusal,
  SaveRefusal,
  saveRefusal,
  saveRefusalMessage,
} from "./save-refusal";
import {
  answerOnce,
  UnsavedEditsAnswer,
  unsavedEditsDialog,
} from "./unsaved-edits.dialog";
import { publishedDialog, PublishedFollowUp } from "./published.dialog";
import { textOf } from "@shared/utils/text-like.util";
import { activeRoleIdOf } from "@shared/utils/token-encoding.utils";
import { WriteScope, writeScopeHolds } from "@shared/utils/write-scope.util";
import {
  computeTabItems,
  isTabDirty,
  PRICE_CONFIG_TAB,
  normalizeTabId,
  planSectionSave,
  resolveStepperItems,
  scopeOfTab,
  tabDisplayLabel,
} from "./tab-algebra.util";
import { PriceOffsetBadge } from "../../price-offset-badge/price-offset-badge";
import { FieldFocus, FieldTarget } from "../field-focus";

@Component({
  selector: "app-enrichment-edit-package",
  imports: [
    PriceOffsetBadge,
    CommonModule,
    FormsModule,
    ButtonComponent,
    DateTimePickerComponent,
    OmniarkUiModule,
    RadioComponent,
    StatusComponent,
    EnrichmentPreviewPanel,
    EnrichmentEditPackageHero,
    EnrichmentPricing,
    EnrichmentShelf,
    EnrichmentSpecialPromotion,
    EnrichmentPackageSettings,
    EnrichmentEditPackageStepper,
    EnrichmentEditPackageTour,
    TabReadinessBanner,
    SectionSaveBar,
  ],
  providers: [
    EnrichmentDirtyTracker,
    EnrichmentPriceDirtyStore,
    EnrichmentPriceLockStore,
    EnrichmentIsSofEditStore,
    EnrichmentDisplayQuantityLockStore,
    SectionDirtyStore,
    EnrichmentPricingGroupStore,
    EnrichmentSaveCoordinator,
    PublishDrawerController,
    ActivePriceStore,
    ActivePriceReadiness,
    AgreementContentStore,
  ],
  templateUrl: "./enrichment-edit-package.html",
  styleUrls: ["./enrichment-edit-package.scss"],
})
export class EnrichmentEditPackage implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly listJourney = inject(EnrichmentListJourneyStore);
  private readonly enrichmentService = inject(EnrichmentService);
  private readonly featureFlags = inject(FeatureFlagService);
  private readonly omniarkModal = inject(OmniarkModalService);
  private readonly enrichmentDataService = inject(EnrichmentDataService);
  private readonly overlayService = inject(OverlayService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly toast = inject(OmniarkToastService);
  private readonly permissionService = inject(PermissionService);
  private readonly authService = inject(AuthService);

  private readonly dirty = inject(EnrichmentDirtyTracker);
  private readonly priceDirty = inject(EnrichmentPriceDirtyStore);
  private readonly priceLock = inject(EnrichmentPriceLockStore);
  private readonly isSofEdits = inject(EnrichmentIsSofEditStore);
  private readonly sectionDirty = inject(SectionDirtyStore);
  private readonly saver = inject(EnrichmentSaveCoordinator);
  private readonly drawerController = inject(PublishDrawerController);
  protected readonly active = inject(ActivePriceStore);
  protected readonly readiness = inject(ActivePriceReadiness);

  readonly languageMode = inject(LanguageModeService);
  readonly languageConfig = inject(LanguageConfigStore);
  private readonly languageService = inject(LanguageService);
  private readonly channelDirectory = inject(EnrichmentChannelDirectory);
  private readonly channelLinks = inject(ChannelLinkService);
  private readonly roleService = inject(RoleService);

  @ViewChild("shortcutHelpDialog")
  private shortcutHelpDialogRef?: ElementRef<HTMLElement>;

  constructor() {
    // Move focus into the shortcut help dialog whenever it opens, and return
    // focus to the previously focused element when it closes. Keeps keyboard
    // users from being stranded outside an open modal.
    effect(() => {
      const open = this.shortcutHelpOpen();
      if (open) {
        const previouslyFocused = document.activeElement as HTMLElement | null;
        this.previousFocus = previouslyFocused;
        // Defer one tick so the dialog node is in the DOM.
        queueMicrotask(() => this.shortcutHelpDialogRef?.nativeElement.focus());
      } else if (this.previousFocus) {
        this.previousFocus.focus?.();
        this.previousFocus = null;
      }
    });

    // The active tab must be a step the stepper offers: Special promotion
    // leaves when the channel stops being myAIS, and the price tabs leave on
    // a package with no prices. Otherwise the pane lands on the not-found card.
    effect(() => {
      const tabs = this.dataTab();
      const current = this.activeTab();
      if (tabs.length && !tabs.some((t) => t.id === current)) {
        this.activeTab.set(tabs[0].id);
      }
    });

    // The breadcrumb names the price, so it follows a switch.
    effect(() => {
      this.active.entry();
      this.pushBreadcrumbs();
    });

    // Once the scope moved and nothing is in flight that the answer would
    // cut short, or that may itself put the scope back: the package a link
    // opened on another channel is reloaded there, anything else goes back
    // to the list. An open prompt holds the guard's veto, so the answer
    // waits for it too.
    effect(() => {
      const change = this.scopeChange();
      if (
        change === null ||
        this.sectionSaving() ||
        this.navigating() ||
        this.promptOpen()
      ) {
        return;
      }
      untracked(() => {
        this.scopeChange.set(null);
        if (!this.scopeMoved()) return;
        if (change === "landed") this.loadProductOffering(this.packageId);
        else this.returnToList();
      });
    });

    // The language set follows the channel the editor is pinned to, which
    // moves with each load and with the first role to arrive after a load
    // that ran before any role. A channel whose set is held, or being read,
    // is not read again; one whose read failed is, by the next load or pin
    // move onto it. A read a later channel overtakes is dropped.
    toObservable(this.loadedScope)
      .pipe(
        map((scope) => scope.channel),
        filter((channel) => {
          const set = this.languageSet();
          return (
            set === null || set.channel !== channel || set.status === "failed"
          );
        }),
        tap((channel) => this.languageSet.set({ channel, status: "reading" })),
        switchMap((channel) =>
          this.channelLanguages(channel).pipe(
            map((languages) => ({
              channel,
              languages,
              status: "held" as const,
            })),
            catchError(() =>
              of({ channel, languages: null, status: "failed" as const }),
            ),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe(({ channel, languages, status }) => {
        if (languages) this.languageConfig.setFromApi(languages);
        this.languageSet.set({ channel, status });
      });
  }

  private previousFocus: HTMLElement | null = null;

  readonly languageOptions: { code: LanguageModeValue; label: string }[] = [
    { code: "ALL", label: "All" },
    { code: "TH", label: "TH" },
    { code: "EN", label: "EN" },
    { code: "MY", label: "MM" },
    { code: "KH", label: "KH" },
  ];

  setLanguageMode(code: LanguageModeValue): void {
    this.languageMode.set(code);
  }

  @ViewChild("publishReviewTemplate")
  publishReviewTemplate!: TemplateRef<unknown>;

  /** Body of the pre-save confirm when an on-sale price may be kept on sale
   *  (Keep on sale / Take off sale radios). See {@link confirmPriceSave}. */
  @ViewChild("priceSaveConfirmTemplate")
  priceSaveConfirmTemplate!: TemplateRef<unknown>;
  /** The radio choice in that dialog — defaults to Keep on sale each time it opens. */
  readonly priceSaveLiveChoice = signal<"keep" | "offSale">("keep");
  /** Copy the open choice dialog renders (option labels/hints); null when closed. */
  readonly priceSaveConfirmCopy = signal<SaveConfirmCopy | null>(null);

  packageId = "";
  productOffering: IProductOffering | null = null;
  productSpecification: IProductSpecification | null = null;
  mainProductOfferingPrice: IProductOfferingPrice | null = null;
  loading = false;

  /** The latest read of a channel's language set, which the pane is built
   *  on: being read, held once it answered, or failed. Null until the
   *  first read starts. A failed read is retried by the next load or pin
   *  move onto its channel. */
  private readonly languageSet = signal<{
    readonly channel: string;
    readonly status: LanguageSetStatus;
  } | null>(null);

  /** Where the pinned channel's language set stands. No write that carries
   *  the package's content goes out until it is held. The pin moves as soon
   *  as the role does, and the read of its channel starts only at the next
   *  change detection, so a latest read that is another channel's counts as
   *  reading. */
  private readonly languageSetStatus = computed<LanguageSetStatus>(() => {
    const set = this.languageSet();
    return set?.channel === this.loadedScope().channel ? set.status : "reading";
  });

  /** True while the language set of the pinned channel is read.
   *  Tab FormGroups iterate `LanguageConfigStore.active()` when they're
   *  constructed, so rendering them before the store is seeded would
   *  produce multilingual forms that won't pick up the channel's set
   *  later (FormGroup doesn't reconcile control names dynamically). The
   *  template ANDs this into the shell's `loading` gate so the tabs, and
   *  the save bar with them, only mount once that set is in. A failed read
   *  opens them on the set already held, with every save refused. */
  protected get languagesLoading(): boolean {
    return this.languageSetStatus() === "reading";
  }

  private readonly lifecycleStatus = signal<string | undefined>(undefined);

  /** The flat prices — the per-price lifecycle facts every publish/reject
   *  gate reads since the POP-lifecycle cutover. Empty for non-flat models. */
  private readonly flatPrices = computed(() => {
    const model = this.enrichmentSignal();
    return model && isFlatEnrichmentModel(model)
      ? (model.__detail.prices ?? [])
      : [];
  });

  readonly hasPublishablePrice = computed<boolean>(() => {
    const price = this.active.price();
    return !!price && lifecycleActions(price).publish.ok;
  });

  /** True when CPC has closed this package (`retired` / `obsolete`).
   *
   *  The PO's own status is read-only here — the portal never transitions it —
   *  but it still vetoes Publish: putting a staged price on sale under a
   *  package that has left the catalogue is not something the user should be
   *  offered, however clean its readiness is. It is deliberately a PACKAGE
   *  fact, not a per-price one: `hasPublishablePrice` already handles a price
   *  that is itself closed (only `designed` is publishable), whereas this
   *  closes the whole editor's publish path.
   *
   *  Reject is NOT gated on it — a price left live under a closed package is
   *  exactly one someone may still need to pull off sale. */
  readonly isPackageClosed = computed<boolean>(() =>
    isClosedStatus(this.lifecycleStatus()),
  );

  /** The user holds `enrichment:publish` — the same gate the BE enforces on
   *  PATCH /enrichment/publish. Without it the hero hides Publish instead of
   *  offering a button whose only outcome is a 403. */
  readonly mayPublish = computed<boolean>(() =>
    this.permissionService.permissions().has(Permissions.EnrichmentPublish),
  );

  readonly mayKeepLiveOnSave = computed<boolean>(() =>
    mayKeepLiveOnSave(this.permissionService, this.featureFlags),
  );

  /** True when the active price is on sale and the user may take it off.
   *  Both halves are required and neither implies the other:
   *  - lifecycle — reject is possible while the price is `active`/`launched`
   *    (the PO's own status is CPC-owned and not consulted);
   *  - permission — `enrichment:reject` is the withdraw half's own gate
   *    (split off `enrichment:publish` so product can restrict pulling
   *    prices off sale to fewer roles), and the list page hides (not
   *    disables) Reject for users without it. Same treatment here:
   *    permission is all-or-nothing per user, so there is no state the user
   *    could reach that would make a greyed entry informative. */
  readonly canReject = computed<boolean>(() => {
    const price = this.active.price();
    return (
      !!price &&
      !this.scopeMoved() &&
      lifecycleActions(price).reject.ok &&
      this.permissionService.permissions().has(Permissions.EnrichmentReject)
    );
  });

  /** The user holds `enrichment:update` — the same gate the BE enforces on the
   *  package and per-price PATCHes behind every save on this screen.
   *
   *  Publish and Reject have been gated since they shipped; Update never was,
   *  so a read-only user could type into any tab, get a live "Save Pricing"
   *  button and only find out at the 403. This is the FE half of that gate:
   *  the contextual bar disables its Save actions and says why. It is not a
   *  read-only mode — the fields still accept input, and the route still lets
   *  a reader in to look — it only stops the write.
   *
   *  Read through `permissions()` (not `hasPermission`) so the computed
   *  re-evaluates on a role switch, exactly as mayPublish/canReject do. */
  readonly mayUpdate = computed<boolean>(() =>
    this.permissionService.permissions().has(Permissions.EnrichmentUpdate),
  );

  /** The scope the package loaded under, pinned by {@link loadProductOffering}
   *  and compared again as each request is built. A load before any role is
   *  selected (the roles arrive after a failed profile) takes the first
   *  selection to come as its own, its channel included: that one is not a
   *  move, and the api had already read the token's role for the load. */
  private readonly loadedScope = linkedSignal<string | null, WriteScope>({
    source: this.roleService.selectionKey,
    computation: (selection, previous) => {
      const pinned = previous?.value ?? this.writeScope();
      if (pinned.selection !== null || selection === null) return pinned;
      // The first selection joins the pin; the token stays the load's.
      return { ...this.writeScope(), tokenRoleId: pinned.tokenRoleId };
    },
  });

  /** True once the role or channel on screen moved since the load: from the
   *  header, from a switch in another tab, or by a link into another channel
   *  of this package. */
  readonly scopeMoved = computed<boolean>(() => {
    const loaded = this.loadedScope();
    return (
      loaded.selection !== this.roleService.selectionKey() ||
      loaded.channel !== this.effectiveChannel()
    );
  });

  /** The scope a request built now would act in. */
  private writeScope(): WriteScope {
    return {
      selection: this.roleService.selectionKey(),
      screenRoleId: this.roleService.selectedRole()?.roleId ?? null,
      tokenRoleId: activeRoleIdOf(this.authService.getAccessToken()),
      channel: this.effectiveChannel(),
    };
  }

  readonly saveRefusalMessage = computed<string | null>(() => {
    const refusal = saveRefusal({
      scopeMoved: this.scopeMoved(),
      packageClosed: this.isPackageClosed(),
      mayUpdate: this.mayUpdate(),
      languageSet: this.languageSetStatus(),
    });
    return refusal ? this.refusalMessage(refusal) : null;
  });

  private refusalMessage(refusal: SaveRefusal): string {
    const acting = this.actingRole();
    const pinned = this.loadedScope().channel;
    return saveRefusalMessage(refusal, {
      role: acting.role ?? "",
      channel: acting.channel
        ? this.channelDirectory.getLabel(acting.channel)
        : "",
      editorChannel: this.channelLabel(),
      languageChannel: this.channelDirectory.getLabel(pinned) || pinned,
    });
  }

  /** The role and channel the next request acts as: the selection on
   *  screen, unless the token already acts as another role. A switch in
   *  another tab reaches the token before the screen. */
  private actingRole(): { readonly role?: string; readonly channel?: string } {
    const shown = this.roleService.selectedRole();
    const tokenRoleId = activeRoleIdOf(this.authService.getAccessToken());
    if (tokenRoleId === null || tokenRoleId === shown?.roleId) {
      return {
        role: shown?.roleName,
        channel: this.roleService.activeChannel()?.channelName,
      };
    }
    const profile = this.roleService
      .availableRoleProfiles()
      .find((p) => p.roleId === tokenRoleId);
    return {
      role: profile?.roleName,
      channel: profile?.channels[0]?.channelName,
    };
  }

  readonly canSave = computed<boolean>(
    () => this.saveRefusalMessage() === null,
  );

  readonly packageContext = computed<HeroPackage | null>(() => {
    if (this.active.state().kind === "loading") return null;
    return {
      id: this.active.packageId(),
      name:
        textOf(this.enrichmentSignal()?.productOffering.name).trim() ||
        "Untitled package",
      status: packageStatusView(this.lifecycleStatus()),
    };
  });

  readonly channelLabel = computed(() => {
    const channel = this.editingChannel();
    return this.channelDirectory.getLabel(channel) || channel;
  });

  /**
   * Whether the live-preview rail is shown. Honoured at EVERY breakpoint — the
   * CSS decides the rail's *width* per breakpoint, this signal alone decides
   * its *visibility*. (Before, `--preview-open` only existed inside the narrow
   * media query, so on desktop the hero button said "Show preview" while the
   * rail was plainly visible and clicking it did nothing.)
   *
   * Seeded from the user's stored preference; with none, the viewport decides —
   * open where there is room for a 460px rail, closed where there isn't.
   */
  showPreview = signal(loadPreviewPref() ?? defaultPreviewOpen());

  /** Needed only to dismiss the panel's full-screen overlay when the rail is
   *  hidden — see {@link togglePreview}. */
  @ViewChild(EnrichmentPreviewPanel)
  private previewPanel?: EnrichmentPreviewPanel;

  /** What the review drawer's "Proceed" runs when confirmed — `publish`/
   *  `reject` of the active price. Aliased to the controller's mode signal —
   *  `openDrawer` is the single writer. */
  readonly drawerMode = this.drawerController.mode;

  /** Drawer surface (template aliases — the controller owns the state; see
   *  PublishDrawerController). */
  readonly drawerTarget = this.drawerController.target;
  readonly drawerCanProceed = this.drawerController.canProceed;
  readonly drawerScheduleEnabled = this.drawerController.scheduleEnabled;
  readonly drawerTiming = this.drawerController.timing;
  readonly drawerScheduledAtLocal = this.drawerController.scheduledAtLocal;
  readonly drawerScheduledAtIsPast = this.drawerController.scheduledAtIsPast;
  readonly drawerScheduleZoneLabel = this.drawerController.scheduleZoneLabel;
  readonly drawerOpenScheduleHint = this.drawerController.openScheduleHint;

  /** enr-radio's `(change)` fires TWICE per click — the component's own value
   *  emit and the bubbled DOM Event — so only the string is the user's
   *  answer, and the reason this is a SET rather than a flip. */
  onDrawerTimingChange(value: unknown): void {
    if (typeof value !== "string") return;
    this.drawerController.setTiming(value === "later" ? "later" : "now");
  }

  setDrawerScheduledAt(value: string): void {
    this.drawerController.setScheduledAtLocal(value);
  }

  /** Forwarded from EnrichmentDirtyTracker. */
  get isDirty() {
    return this.dirty.isDirty;
  }

  private readonly enrichmentSignal = toSignal(
    this.enrichmentDataService.enrichment$,
    { initialValue: null },
  );

  /** Tab definitions with live badge counts derived from enrichment data.
   *  Special promotion (Net X2/X3) is myAIS-only. `isExactMyAisChannel` —
   *  NOT isMyAisChannel(), which treats an unpicked channel as myAIS: the BE
   *  accepts pricingLogicAlgorithm only for an exact myAIS save scope, so
   *  showing the form on an empty scope would silently drop the input. A
   *  package without prices offers only the tabs that edit the package. */
  dataTab = computed<TabItem[]>(() => {
    const tabs = computeTabItems(this.enrichmentSignal());
    const channel = this.editingChannel();
    const packageOnly = this.active.state().kind === "no-prices";
    return tabs.filter(
      (t) =>
        (!packageOnly || scopeOfTab(t.id) !== "price") &&
        (t.id !== "special-promotion" || isExactMyAisChannel(channel)),
    );
  });

  /** Currently active tab id */
  activeTab = signal<string>("price-config");

  readonly pane = computed<"spinner" | "refusal" | "editor">(() => {
    const kind = this.active.state().kind;
    if (kind === "loading" || kind === "redirect") return "spinner";
    return kind === "not-on-package" ? "refusal" : "editor";
  });

  readonly refusedPriceId = computed<string>(() => {
    const state = this.active.state();
    return state.kind === "not-on-package" ? state.requested : "";
  });

  openFirstPrice(): void {
    const first = this.active.entries()[0];
    if (first) void this.active.goTo(first.id);
  }

  /** One pane per (tab, price): a price tab is re-created when the price
   *  changes, like a tab switch, so no tab carries another price's expanded
   *  cards, locks, scroll-spy or form state. A package tab is not keyed by
   *  price, so a price switch does not rebuild it. */
  readonly paneKey = computed<string>(() => {
    const tab = this.activeTab();
    return scopeOfTab(tab) === "price"
      ? `${tab}:${this.active.popId() ?? ""}`
      : tab;
  });

  /** Channel currently being edited (drives the `x-channel` header). Mirrors
   *  `EnrichmentStateStore.channel$` so the hero shows the user which view
   *  they are authoring. Empty string = no channel picked. */
  readonly editingChannel = toSignal(this.enrichmentDataService.channel$, {
    initialValue: "" as ReturnType<EnrichmentDataService["getChannel"]>,
  });

  /** The channel the api resolves a request to: a bound role's own, whatever
   *  `x-channel` says; the one sent, for a role on all channels or none. */
  private readonly effectiveChannel = computed<string>(
    () => this.channelLinks.roleChannelValue() || this.editingChannel(),
  );

  // ── Auto-save indicator (Phase 4.2) ──────────────────────────────────────
  // The dirty tracker owns `saveState` + `lastSavedAt`; the shell only adds
  // a ticker that advances the "Saved N min ago" copy.
  private readonly relativeNowMs = signal(Date.now());
  private relativeTickerId: ReturnType<typeof setInterval> | null = null;

  /** Snapshot of save state forwarded to the hero. */
  readonly saveState = computed(() => this.dirty.saveState());
  readonly lastSavedAt = computed(() => this.dirty.lastSavedAt());
  readonly relativeNow = computed(() => this.relativeNowMs());

  /** The workflow steps shown in the stepper bar. Status is driven by
   *  the readiness summary when available so the indicator color reflects
   *  blockers/warnings/lang-gaps live without a separate badge. Passes the
   *  section-status map so a clean tab (absent from the readiness map) can
   *  still render `complete` — keeping the green check in sync with the donut. */
  readonly visibleSteps = computed(() =>
    resolveStepperItems(
      this.dataTab(),
      this.readiness.byTab(),
      this.readiness.statusByTab(),
    ).map((s) => ({ ...s, dirty: this.isTabDirty(s.id) })),
  );

  /** Readiness summary for the currently-active tab — feeds the banner. */
  readonly activeTabReadiness = computed(() =>
    this.readiness.byTab().get(this.activeTab()),
  );

  /** Human-readable label of the active tab — used in the banner's aria-label. */
  readonly activeTabLabel = computed(() => {
    const tabId = this.activeTab();
    return this.visibleSteps().find((s) => s.id === tabId)?.label ?? "";
  });

  ngOnInit(): void {
    this.drawerController.bindFocusField((target) => this.focusField(target));
    this.watchScopeChanges();
    this.dirty.startTracking();

    // Phase 4.2 — advance the relative-time signal every 30s so the
    // "Saved N min ago" pill stays current without burning cycles on
    // sub-second resolution.
    this.relativeTickerId = setInterval(() => {
      this.relativeNowMs.set(Date.now());
    }, 30_000);

    // Phase 4.5 — auto-show onboarding tour on first visit. Delay so the
    // hero + stepper anchors are mounted and measurable.
    if (!EnrichmentEditPackageTour.hasSeen()) {
      setTimeout(() => this.tourOpen.set(true), 600);
    }

    // Push the editor breadcrumb to the global page header. The current
    // package name is unknown until `loadProductOffering` resolves, so we
    // start with a placeholder and refresh again from `pushBreadcrumbs`.
    this.pushBreadcrumbs();

    this.route.paramMap
      .pipe(
        map((params) => params.get("id")),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((id) => {
        this.packageId = id ?? "";
        if (id) this.loadProductOffering(id);
      });

    this.enrichmentDataService.editorAction$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((action) => this.focusField(action));
  }

  ngOnDestroy(): void {
    this.breadcrumbService.clear();
    if (this.relativeTickerId !== null) {
      clearInterval(this.relativeTickerId);
      this.relativeTickerId = null;
    }
  }

  // ── Keyboard shortcuts (Phase 4.6) ────────────────────────────────────────
  // Power-user shortcuts so marketing doesn't need to mouse back to the hero
  // every save. Editable elements opt-out of the global handlers so the user
  // can still press Ctrl+S inside, e.g., the publish-drawer text inputs
  // without triggering a save (the modifier passthrough is preserved by
  // not preventing default in that branch).
  readonly shortcutHelpOpen = signal(false);

  // ── Onboarding tour (Phase 4.5) ───────────────────────────────────────────
  // First-visit detection happens in ngOnInit; the marketer can replay the
  // tour from the shortcut help overlay later (via `restartTour()`).
  readonly tourOpen = signal(false);

  /** True when the keypress originated from an editable surface (input,
   *  textarea, select, contenteditable). We let those handle their own
   *  shortcuts so the editor doesn't hijack typing. */
  private isEditableTarget(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;
    if (!target) return false;
    const tag = target.tagName?.toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select") return true;
    if (target.isContentEditable) return true;
    return false;
  }

  /** True when the key was pressed inside a popover widget (a menu, a
   *  listbox, their trigger, anything in the CDK overlay) or was already
   *  handled: those own their arrow keys, and the step shortcut must not
   *  switch the tab behind them. */
  private insidePopover(event: KeyboardEvent): boolean {
    if (event.defaultPrevented) return true;
    const target = event.target as HTMLElement | null;
    return !!target?.closest?.(
      '.cdk-overlay-container, [role="menu"], [role="listbox"], [aria-haspopup]',
    );
  }

  @HostListener("document:keydown", ["$event"])
  protected onShortcut(event: KeyboardEvent): void {
    const key = event.key;
    const cmd = event.metaKey || event.ctrlKey;

    // Cmd/Ctrl + S — Save the active section (per-section save cutover).
    // Gated on sectionSaving() like the SaveBar's click handler, so mashing
    // the shortcut can't stack a second save on top of one still in flight.
    if (cmd && !event.shiftKey && key === "s") {
      event.preventDefault();
      if (!this.loading && !this.sectionSaving()) this.onSectionSave();
      return;
    }

    // ? — Toggle shortcut help overlay (only outside text inputs)
    if (!cmd && key === "?" && !this.isEditableTarget(event)) {
      event.preventDefault();
      this.shortcutHelpOpen.update((v) => !v);
      return;
    }

    // Escape — close help overlay
    if (key === "Escape" && this.shortcutHelpOpen()) {
      event.preventDefault();
      this.shortcutHelpOpen.set(false);
      return;
    }

    // Escape — dismiss the narrow-screen preview drawer. Only there: on desktop
    // the rail is a docked column, not a modal, and Escape must not collapse it.
    //
    // The `expanded()` guard keeps the two Escape handlers from firing on one
    // keypress — the panel owns Escape while its full-screen overlay is up, and
    // without this the same press would close the overlay AND hide the rail.
    if (key === "Escape" && this.isPreviewDrawerDismissable()) {
      event.preventDefault();
      this.togglePreview();
      return;
    }

    // ← / → — Previous/next customer-facing step (skip when editing text)
    if (
      !cmd &&
      !event.shiftKey &&
      !event.altKey &&
      (key === "ArrowLeft" || key === "ArrowRight") &&
      !this.isEditableTarget(event) &&
      !this.insidePopover(event)
    ) {
      this.navigateStep(key === "ArrowRight" ? 1 : -1);
      // Don't preventDefault — arrow keys still useful elsewhere if we miss.
    }
  }

  /** Move forward/backward through visibleSteps() (Stepper bar). Wraps at
   *  the edges of the list — marketing typically loops through tabs as
   *  they polish content. */
  private navigateStep(delta: 1 | -1): void {
    const steps = this.visibleSteps();
    if (steps.length === 0) return;
    const currentId = this.activeTab();
    const idx = steps.findIndex((s) => s.id === currentId);
    if (idx === -1) return;
    const next = (idx + delta + steps.length) % steps.length;
    const nextId = steps[next]?.id;
    if (nextId) this.onTabChange(nextId);
  }

  closeShortcutHelp(): void {
    this.shortcutHelpOpen.set(false);
  }

  // ── Tour controls (Phase 4.5) ─────────────────────────────────────────────
  onTourDone(): void {
    this.tourOpen.set(false);
  }

  /** Replay the tour from the shortcut help dialog or any other entry point. */
  restartTour(): void {
    this.shortcutHelpOpen.set(false);
    EnrichmentEditPackageTour.reset();
    // Brief deferral so the dialog close animation doesn't fight the
    // tour's first scrollIntoView.
    setTimeout(() => this.tourOpen.set(true), 80);
  }

  /** Sync the global header breadcrumb to "Package display editor / <pkg> /
   *  <price>". Called once at init and again whenever the package or the
   *  active price changes. */
  private pushBreadcrumbs(): void {
    const crumbs = [
      {
        label: "Package display editor",
        route: APP_ROUTES.MANAGEMENT.ENRICHMENT_PRICE.ROOT,
      },
      { label: this.breadcrumbDisplayName },
    ];
    const price = this.active.entry();
    if (price) crumbs.push({ label: price.title });
    this.breadcrumbService.setBreadcrumbs(crumbs);
  }

  /** The header switcher, ‹ ›, the published dialog and any "review that
   *  price" link: navigate, then start the new price at the top. Focus
   *  moves to the title unless the switch came from the header's own
   *  controls (`fromHeader`): those survive the switch, and a keyboard user
   *  pressing Next six times must not be sent back to the title each time. */
  onPriceSelect(popId: string, fromHeader = false): void {
    void this.active.goTo(popId).then((moved) => {
      if (moved) this.afterSwitch(fromHeader);
    });
  }

  private afterSwitch(keepFocus: boolean): void {
    document.querySelector(".page-content")?.scrollTo({ top: 0 });
    if (keepFocus) return;
    const title = document.querySelector<HTMLElement>(".hero__title");
    title?.focus({ preventScroll: true });
  }

  /**
   * The editor is of one channel. When the role or channel on screen moves
   * — from the header, or because the role was switched in another tab —
   * what is open here is no longer that channel's: back to the list, which
   * loads under the new one. Unsaved edits get the usual leave prompt. The
   * one move that keeps the editor is a link into another channel of this
   * same package: the guard wrote that channel into the store (nothing else
   * writes it while the editor is up), and the package is reloaded under
   * it, which pins the scope anew.
   *
   * Not before a save, a navigation or a prompt under way has settled: the
   * guard would refuse the leave with its in-flight toast, for a navigation
   * the user did not start, an open prompt holds the guard's veto, and a
   * navigation's own guards may be what moved the role, on the way into a
   * link that needs another one. So the answer waits (the constructor
   * effect on {@link scopeChange}) and then acts only if the scope is still
   * not the one the package loaded under: a link to another package has
   * pinned it anew on its way in, a price switch or a cancelled navigation
   * has not.
   */
  private watchScopeChanges(): void {
    this.roleService.channelChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.scopeChange.set("moved"));
    this.enrichmentDataService.channel$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.scopeChange.set("landed"));
  }

  /** The last thing that moved the scope, until it is answered. */
  private readonly scopeChange = signal<"moved" | "landed" | null>(null);

  /** True from a navigation's start to the event that ends it, however it
   *  ends. */
  private readonly navigating = toSignal(
    this.router.events.pipe(
      filter(
        (event) =>
          event instanceof NavigationStart ||
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError ||
          event instanceof NavigationSkipped,
      ),
      map((event) => event instanceof NavigationStart),
    ),
    { initialValue: this.router.getCurrentNavigation() !== null },
  );

  // ── Loading ────────────────────────────────────────────────────────────────

  /** The package GET of the latest load. The next load drops it, so an
   *  older answer can never land over a newer one and the package held,
   *  the scope pinned and the language set read always describe the same
   *  load. */
  private inFlightLoad: Subscription | null = null;

  private loadProductOffering(id: string): void {
    this.inFlightLoad?.unsubscribe();
    this.loading = true;
    this.loadedScope.set(this.writeScope());
    this.dirty.beginLoad();
    // A (re)load starts a fresh edit session — nothing is dirty yet.
    this.priceDirty.clear();
    this.priceLock.clear();
    this.isSofEdits.clear();
    this.sectionDirty.clearTab();
    // The root store keeps the previous package's model after the editor is
    // left; without this a bare link to package Y would resolve its first
    // price against package X. `beginLoad()` above mutes the dirty tracker.
    this.enrichmentDataService.setEnrichment(null);

    // Single flat GET — replaces the legacy PO + N×POP fan-out. The raw
    // EnrichmentDetail is kept verbatim so untouched passthrough data
    // round-trips unchanged through the PATCH save.
    this.inFlightLoad = this.enrichmentService
      .getEnrichmentById(id, { ...ENRICHMENT_HEADER_TRANSPORT })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          try {
            const code =
              res.resultCode ?? (res as unknown as { code?: string }).code;
            const isSuccess = code === "20000" || code === "200";
            const detail = res?.data;
            if (!isSuccess || !detail || Object.keys(detail).length === 0) {
              this.showNotOnChannel(id);
              return;
            }
            if (
              packageOwner(detail.productLine, this.effectiveChannel()) ===
              "online-store"
            ) {
              this.handToPackageEditor(id);
              return;
            }

            const model = detailToModel(detail);
            this.productOffering = model.productOffering;
            this.productSpecification = null;
            this.mainProductOfferingPrice =
              model.productOfferingPrice?.[0] ?? null;

            this.enrichmentDataService.setEnrichment(model);
            // Fresh discard target for this edit session — every section
            // save refreshes it again on success (per-section save cutover).
            this.sectionDirty.setSnapshot(model);

            this.lifecycleStatus.set(detail.lifecycleStatus);
            this.pushBreadcrumbs();
            this.dirty.endLoad();
          } catch (err) {
            // Surface the real error and unblock the UI — otherwise an
            // exception between setEnrichment() and `loading=false` leaves
            // the spinner stuck forever.
            logger.error(
              "[enrichment-edit-package] post-load handling threw:",
              err,
            );
            this.showErrorModal(
              "Loaded the package but rendering failed. Check the logger for details.",
            );
          } finally {
            this.loading = false;
          }
        },
        error: (error) => {
          logger.error("Error loading product offering:", error);
          this.loading = false;
          const status = (error as HttpErrorResponse | null)?.status;
          if (status === 404) {
            this.showNotOnChannel(id);
          } else if (status === 403) {
            this.showErrorModal(
              "You do not have permission to open this product.",
              true,
            );
          } else {
            this.showErrorModal(
              "Failed to load product offering. Please try again.",
            );
          }
        },
      });
  }

  /** The channel's own authoring language set,
   *  `GET /api/v1/channel/{channelId}/language`. That endpoint is keyed by
   *  the numeric id, which only the channel list maps from the key the
   *  editor carries, so the read waits for the list. With no channel, one
   *  the list does not know, or one with no languages linked, it is every
   *  active master language, the same set the BFF projects the detail onto.
   *  Errors when the list or the set cannot be read. */
  private channelLanguages(channel: string): Observable<ApiLanguage[]> {
    return this.channelDirectory
      .load()
      .pipe(
        switchMap(() =>
          this.languageService.getEditorLanguages(
            this.channelDirectory.getChannelId(channel),
          ),
        ),
      );
  }

  // ── Modals ────────────────────────────────────────────────────────────────

  /** `leave`: there is nothing to edit behind the message — back to the
   *  list once it is read. */
  private showErrorModal(message: string, leave = false): void {
    const closed = this.omniarkModal.showModal({
      title: "Error",
      message,
      primaryButtonText: "OK",
      primaryButtonVariant: "primary",
      showCloseButton: false,
      backdropDismiss: true,
    });
    if (!leave) return;
    closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.returnToList(),
      error: () => this.returnToList(),
    });
  }

  /** A link reached a package the package editor owns on this channel:
   *  open it there, since the flat api refuses writes to it. */
  private handToPackageEditor(id: string): void {
    const link = packageEditorLink(
      id,
      this.route.snapshot.paramMap.get("popId"),
      "enrichment-price",
    );
    this.router.navigate(link.commands, {
      queryParams: link.queryParams,
      replaceUrl: true,
    });
  }

  /** The api found no such product for the channel the editor is on: say
   *  which channel, since the same id can be on another one. */
  private showNotOnChannel(id: string): void {
    const channel = this.channelDirectory.getLabel(
      this.enrichmentDataService.getChannel(),
    );
    this.showErrorModal(
      channel ? `${id} is not on ${channel}.` : `${id} was not found.`,
      true,
    );
  }

  /** After a publish the editor stays on the price: the dialog offers the
   *  next staged sibling and the list, and closing it means "stay". */
  private showPublishedModal(): void {
    const next = nextToPublish(
      this.active.entries(),
      this.active.prices(),
      this.active.popId(),
    );
    const dialog = publishedDialog(
      this.active.entry()?.title ?? "This price",
      this.channelLabel(),
      next,
    );
    answerOnce(this.omniarkModal.showModal(dialog.config), undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((answer) => {
        const followUp: PublishedFollowUp =
          answer === "primary"
            ? dialog.primary
            : answer === "secondary"
              ? dialog.secondary
              : "stay";
        if (followUp === "next" && next) this.onPriceSelect(next.id);
        else if (followUp === "list") this.returnToList();
      });
  }

  /** Return to the original query after Publish/Reject. Direct edit links
   * have no list journey, so keep the package-id search as their fallback. */
  protected returnToList(): void {
    this.router.navigate([APP_ROUTES.MANAGEMENT.ENRICHMENT_PRICE.ROOT], {
      queryParams: this.listJourney.hasReturnState ? {} : { q: this.packageId },
    });
  }

  // ── User actions ──────────────────────────────────────────────────────────

  /** True only when the preview is currently a modal-ish overlay drawer —
   *  open, below the rail breakpoint, and not already showing its own
   *  full-screen surface (which owns Escape itself). */
  private isPreviewDrawerDismissable(): boolean {
    if (!this.showPreview()) return false;
    if (this.previewPanel?.expanded()) return false;
    try {
      return window.matchMedia(`(max-width: ${PREVIEW_RAIL_BREAKPOINT_PX}px)`)
        .matches;
    } catch {
      // No matchMedia — assume the docked desktop rail, which Escape ignores.
      return false;
    }
  }

  togglePreview(): void {
    const next = !this.showPreview();
    // When expanded, the panel element physically lives in a body-level CDK
    // overlay, not in the rail — collapsing the rail underneath it would leave
    // a full-screen preview whose only dismiss controls are off-screen. Close
    // the overlay first so hiding always lands somewhere coherent.
    if (!next) this.previewPanel?.closeExpand();
    this.showPreview.set(next);
    savePreviewPref(next);
  }

  onTabChange(tabId: string): void {
    const normalized = normalizeTabId(tabId);
    if (normalized === this.activeTab()) return;
    this.confirmUnsavedEdits()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((leave) => {
        if (!leave) return;
        this.activeTab.set(normalized);
        // A tab the user chose has no field to land on; a stale jump must
        // not replay when that tab is next re-created.
        this.fieldFocus.set(null);
      });
  }

  /** The held field request, dropped whenever the active price changes: a
   *  price switch re-creates the tab, and a jump made on one price must
   *  neither replay on a sibling nor on the way back to the price it was
   *  made for. A request set after a switch landed (the Term tab's review of
   *  a price agreement) is kept until the next switch. */
  private readonly fieldFocus = linkedSignal<string | null, FieldFocus | null>({
    source: this.active.popId,
    computation: () => null,
  });
  private focusSeq = 0;

  /** What the owning tab's `[focus]` input sees. */
  readonly paneFocus = this.fieldFocus.asReadonly();

  focusField(target: FieldTarget): void {
    const tabId = normalizeTabId(target.tabId);
    const apply = (): void => {
      this.fieldFocus.set({ ...target, tabId, seq: ++this.focusSeq });
      if (target.sectionId || target.fieldKey) {
        this.enrichmentDataService.setPreviewHighlight(
          target.sectionId ?? target.fieldKey ?? "",
        );
      }
      // Defer to a macrotask so Angular has swapped the tab's template into
      // the DOM before the target is looked up.
      setTimeout(() => this.scrollToReadinessTarget(target), 0);
    };
    if (tabId === this.activeTab()) {
      apply();
      return;
    }
    this.confirmUnsavedEdits()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((leave) => {
        if (!leave) return;
        this.activeTab.set(tabId);
        apply();
      });
  }

  /** Back to where the editor was opened from. Sibling switches replace the
   *  history entry, so this reaches the list. The question is asked before
   *  the history moves: a Stay answered during the popstate would leave the
   *  list's entry overwritten with the editor's. By the time the guard runs
   *  nothing is unsaved, so it does not ask again. */
  onCancel(): void {
    this.confirmUnsavedEdits()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((leave) => {
        if (leave) this.location.back();
      });
  }

  /** Open while the unsaved-edits prompt is on screen: a held arrow key or a
   *  double click must not stack a second modal, and the leave a scope move
   *  asks for waits for the answer. The modal closing re-arms it, not the
   *  save it may start. */
  private readonly promptOpen = signal(false);

  /**
   * The route guard's question for every way off the current price or
   * package, and the tab switch's. Completes with exactly one boolean on
   * every path: true to leave (clean, saved, or discarded), false to stay
   * (save failed or cancelled, dismissed, a save in flight, or a prompt
   * already open). `answerOnce` is what makes "exactly one" hold: omniark
   * errors the modal stream on dismiss, and a guard stream that errors
   * throws a NavigationError instead of staying.
   */
  confirmUnsavedEdits(): Observable<boolean> {
    if (!this.sectionDirty.isDirty() && !this.dirty.isDirty()) return of(true);
    if (this.sectionSaving()) {
      this.toast.showInfo("A save is in progress. Try again in a moment.", {
        duration: 3000,
      });
      return of(false);
    }
    if (this.promptOpen()) return of(false);
    this.promptOpen.set(true);
    const dialog = unsavedEditsDialog(this.saveRefusalMessage());
    return answerOnce(
      this.omniarkModal.showModal(dialog.config),
      undefined,
    ).pipe(
      finalize(() => this.promptOpen.set(false)),
      switchMap((answer) => {
        const chosen: UnsavedEditsAnswer =
          answer === "primary"
            ? dialog.primary
            : answer === "secondary"
              ? dialog.secondary
              : "stay";
        if (chosen === "save") return this.saveActiveSection$();
        if (chosen === "discard") {
          this.discardSectionEdits();
          return of(true);
        }
        return of(false);
      }),
    );
  }

  /** `saveActiveSection` for the prompt. No teardown: the save belongs to
   *  the editor, not to the navigation that asked for it, so a superseded
   *  navigation cannot abort a PATCH in flight. */
  private saveActiveSection$(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      this.saveActiveSection((clean) => {
        subscriber.next(clean);
        subscriber.complete();
      });
    });
  }

  /** Publish action from the hero — opens the review drawer so the user can
   *  audit sections + issues before advancing the lifecycle. Confirm happens
   *  via the drawer's "Proceed" button. */
  onPublish(): void {
    this.drawerController.openDrawer(this.publishReviewTemplate, "publish");
    this.refreshDrawerSchedules();
  }

  /** Reject action from the hero's Publish split menu and the Pricing tab's
   *  pane head — takes the active price off sale for the channel being
   *  edited (`active`/`launched` → `rejected`).
   *
   *  Opens the review drawer in `reject` mode (readiness issues don't gate
   *  this — they matter for putting a price ON sale, not off).
   *
   *  Deliberately still available while the form is dirty, unlike Publish —
   *  the call is status-only and never sends the edit body, so a live price
   *  can be pulled off sale without first having to finish an edit. The
   *  drawer's reject note carries the discard warning the old modal had. */
  onReject(): void {
    if (!this.editingChannel()) {
      // The backend resolves WHICH overlay to reject from `x-channel`; with no
      // channel in scope it would 400, or worse hit one the user didn't mean.
      this.toast.showError("Select a channel before rejecting a price", {
        duration: 3000,
      });
      return;
    }

    this.drawerController.openDrawer(this.publishReviewTemplate, "reject");
    this.refreshDrawerSchedules();
  }

  /** The veto every write shares, read as its request is built: the call
   *  goes out only under the scope the package loaded under, with the token
   *  acting as the role on screen. Otherwise it would land on another
   *  channel's overlay: the token moves before the screen after a switch in
   *  another tab, and keeps acting as the other role when the profile fails
   *  to follow. A write that carries the package's `content` (a save, a
   *  stage) also waits for the pinned channel's language set, which the
   *  pane is built on; a `status` write (publish, reject, schedule) sends
   *  price ids and a date, so it does not. Says why and reports true when
   *  the call must not run. */
  private refuseWrite(write: "content" | "status"): boolean {
    const refusal = !writeScopeHolds(this.writeScope(), this.loadedScope())
      ? "scope-moved"
      : write === "content"
        ? languageSetRefusal(this.languageSetStatus())
        : null;
    if (refusal === null) return false;
    this.toast.showError(this.refusalMessage(refusal), { duration: 4000 });
    return true;
  }

  /** Reject — `PATCH /enrichment/reject`, status-only. Ends the same way
   *  Publish does, back on the list with the package pre-searched, but reports
   *  through a toast rather than a success modal: the modal exists to be
   *  dismissed before navigating, and the confirmation here was already given
   *  on the way in. The toast is app-level, so it survives the route change and
   *  is read on the list, whose row shows Rejected only once every price is:
   *  the toast names the one price that came off sale. */
  private performReject(priceIds: NonEmptyPriceIds): void {
    if (this.refuseWrite("status")) return;
    this.overlayService.showLoading("Rejecting price...");

    this.saver
      .performReject(this.packageId, priceIds, () =>
        this.overlayService.hideLoading(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result.ok) {
          // The PO status is CPC-owned — reject transitions PRICES only, and
          // the coordinator already patched them in the model, so the
          // per-price gates (canReject/hasPublishablePrice) update on their
          // own; nothing re-stamps the package badge.
          // Clear the dirty flag so the guard doesn't re-ask about edits the
          // reject drawer already covered. Discarded, not saved — nothing of
          // theirs was persisted.
          this.dirty.markDiscarded();
          this.priceDirty.clear();
          this.priceLock.clear();
          this.isSofEdits.clear();
          this.sectionDirty.clearTab();
          this.toast.showSuccess(this.rejectedToast(priceIds), {
            duration: 3000,
          });
          this.returnToList();
          return;
        }

        logger.error("Error rejecting enrichment price:", result.raw);
        this.toast.showError(this.rejectErrorMessage(result.raw), {
          duration: 4000,
        });
      });
  }

  /** Same mapping the list page uses, so one failure never reads as two
   *  different problems. 400 = the overlay left `active`/`launched` since the
   *  page loaded; 404 = this channel has no overlay at all. Both mean what is
   *  on screen is stale, so neither is worth a Retry — which is why Reject
   *  reports through a toast where Publish gets a retry modal. */
  private rejectErrorMessage(raw: unknown): string {
    const status = (raw as { status?: number })?.status;
    if (status === 400 || status === 404) {
      return "This price is no longer on sale on this channel — reload the page to see its current status.";
    }
    if (status === 403) {
      return "You do not have permission to reject prices.";
    }
    return this.backendErrorMessage(raw) || "Reject failed. Please try again.";
  }

  /** What came off sale, on which channel, and that its siblings did not:
   *  a reject withdraws one price, and the list row the toast lands beside
   *  still shows the package on sale while any sibling is. */
  private rejectedToast(priceIds: readonly string[]): string {
    const channel = this.channelLabel();
    const siblings = this.active.entries().length - 1;
    const where = channel ? ` on ${channel}` : "";
    const unchanged =
      siblings === 1
        ? " Its other price is unchanged."
        : siblings > 1
          ? ` Its ${siblings} other prices are unchanged.`
          : "";
    return `"${this.priceTitleOf(priceIds)}" is no longer on sale${where}.${unchanged}`;
  }

  /** Review drawer's "Proceed" — closes the drawer and dispatches the
   *  controller's request, which names exactly the active price. `reject`
   *  takes it off sale; `publish` is the dedicated status-only call
   *  (`PATCH /enrichment/publish`) that advances the channel overlay
   *  `designed → active` without re-sending the edit body; a `scheduledAt`
   *  queues either. */
  confirmDrawerAction(): void {
    const request = this.drawerController.request();
    if (!request) return;
    this.drawerController.closeDrawer();
    if (request.scheduledAt) {
      this.performSchedule(
        request.priceIds,
        request.scheduledAt,
        request.action === "reject" ? "unpublish" : "publish",
      );
      return;
    }
    if (request.action === "reject") {
      this.performReject(request.priceIds);
    } else {
      this.performPublish(request.priceIds);
    }
  }

  /**
   * Queue the drawer's action instead of running it.
   *
   * Unlike publish and reject this does NOT leave the page: nothing changed
   * status, the operator is still editing, and the natural next move is to
   * keep working or arm the other direction. The drawer's schedule view is
   * refreshed from the rows that come back so a reopened drawer immediately
   * shows the price as armed.
   */
  private performSchedule(
    priceIds: NonEmptyPriceIds,
    scheduledAt: string,
    direction: "publish" | "unpublish",
  ): void {
    if (this.refuseWrite("status")) return;
    const verb = direction === "unpublish" ? "take-down" : "publish";
    this.overlayService.showLoading(`Scheduling ${verb}...`);

    this.saver
      .performSchedule(this.packageId, priceIds, scheduledAt, direction, () =>
        this.overlayService.hideLoading(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result.ok) {
          const count = result.schedules.length || priceIds.length;
          this.toast.showSuccess(
            `${count} ${count === 1 ? "price" : "prices"} scheduled to ${
              direction === "unpublish" ? "come off sale" : "go on sale"
            } ${formatScheduledAt(scheduledAt)}`,
            { duration: 4000 },
          );
          this.refreshDrawerSchedules();
          return;
        }

        logger.error(`Error scheduling ${verb}:`, result.raw);
        this.toast.showError(this.scheduleErrorMessage(result.raw, direction), {
          duration: 4000,
        });
      });
  }

  /** 409 is the one an operator can act on: the price already holds an open
   *  schedule in this direction, and there is no reschedule — cancel the
   *  existing one first. The rest map to "what you are looking at is stale". */
  private scheduleErrorMessage(
    raw: unknown,
    direction: "publish" | "unpublish",
  ): string {
    const status = (raw as { status?: number })?.status;
    if (status === 409) {
      return `A price is already scheduled to ${
        direction === "unpublish" ? "come off sale" : "go on sale"
      } — cancel that schedule before setting a new one.`;
    }
    if (status === 400 || status === 404) {
      return "These prices are no longer in a state that can be scheduled — reload the page to see where they stand.";
    }
    if (status === 403) {
      return "You do not have permission to schedule this.";
    }
    return (
      this.backendErrorMessage(raw) || "Scheduling failed. Please try again."
    );
  }

  /**
   * Read this offering's schedules into the drawer. Only while the flag is
   * on: with it off the backend does not mount the route at all, so the call
   * would be a guaranteed 404 on every drawer open. A failure is swallowed —
   * the schedules only grey out rows that are already armed, and losing that
   * hint is better than an error toast on a drawer the user opened to publish.
   */
  private refreshDrawerSchedules(): void {
    if (!this.drawerController.scheduleEnabled() || !this.packageId) return;
    this.enrichmentService
      .listPublishSchedules(this.packageId, { ...ENRICHMENT_HEADER_TRANSPORT })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.drawerController.setSchedules(res?.data ?? []),
        error: (err: unknown) =>
          logger.error("Error reading publish schedules:", err),
      });
  }

  /** Kept so the publish-review drawer template (still wired in the
   *  shell HTML) can close itself; no Save coupling. */
  closePublishReview(): void {
    this.drawerController.closeDrawer();
  }

  navigateFromPublishIssue(issue: PublishIssue, fallbackTabId: string): void {
    this.drawerController.navigateToIssue(issue, fallbackTabId);
  }

  /** Banner emits an IssueRef when the user clicks an issue line. */
  onBannerIssueSelect(ref: IssueRef): void {
    this.focusField(ref);
  }

  /** A blank field was clicked in a language coverage card. */
  onCoverageFieldJump(field: MissingField): void {
    this.focusField(field);
  }

  private scrollToReadinessTarget(ref: FieldTarget): void {
    const editor = document.querySelector(
      ".enrichment-editor-pane",
    ) as HTMLElement | null;
    const target =
      (ref.sectionId &&
        document.querySelector(`[data-readiness-target="${ref.sectionId}"]`)) ||
      (ref.fieldKey &&
        document.querySelector(`[data-readiness-target="${ref.fieldKey}"]`));

    if (target instanceof HTMLElement) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      target.classList.add("readiness-target-flash");
      setTimeout(() => target.classList.remove("readiness-target-flash"), 1600);
      return;
    }
    const pricingTabScrollsToItsCard =
      normalizeTabId(ref.tabId) === PRICE_CONFIG_TAB &&
      sectionForTarget(ref) !== null;
    if (pricingTabScrollsToItsCard) return;
    // Fallback — scroll the editor pane to top so the user at least sees
    // they landed on the right tab.
    if (editor) editor.scrollTo({ top: 0, behavior: "smooth" });
  }

  publishIssuePrefix(issue: PublishIssue): string {
    return this.drawerController.publishIssuePrefix(issue);
  }

  getTabDisplayLabel(tabId: string, fallback: string): string {
    return tabDisplayLabel(tabId, fallback);
  }

  get packageDisplayName(): string {
    return textOf(this.productOffering?.name).trim() || "Untitled package";
  }

  /** Title of the price a call names, resolved from the ids it sends rather
   *  than from the active price, so a retried call names what it resends. */
  private priceTitleOf(priceIds: readonly string[]): string {
    return (
      this.active.entries().find((e) => e.id === priceIds[0])?.title ??
      this.packageDisplayName
    );
  }

  get packageHeading(): string {
    return `Edit: ${this.packageDisplayName}`;
  }

  get breadcrumbDisplayName(): string {
    const name = this.packageDisplayName;
    return name.length > 40 ? `${name.slice(0, 40)}…` : name;
  }

  // ── Save flow ──────────────────────────────────────────────────────────────

  /** True when the active price carries unsaved edits. Only the active
   *  price counts: a stray sibling id must not light a tab that cannot save
   *  it, and a save never sends a price the page is not on. */
  private readonly activePriceDirty = computed<boolean>(() => {
    const id = this.active.popId();
    return !!id && this.priceDirty.isDirty(id);
  });

  /** True while a per-section save (triggered from `SectionSaveBar`) is in
   *  flight. Deliberately separate from `dirty.saveState()`/the overlay —
   *  those drive the hero's whole-page "Saving…" pill and blocking modal,
   *  which per-section saves intentionally skip: the point of the cutover
   *  is a lightweight, non-blocking save scoped to the tab the user is on.
   *  This signal is local to `SectionSaveBar`'s own in-flight state. */
  readonly sectionSaving = signal(false);

  /** Whether the given tab carries unsaved edits — see `isTabDirty` in
   *  `tab-algebra.util.ts` for the scope rules. Shared by `sectionBarDirty`
   *  (active tab only) and the stepper's per-step dirty dot (every
   *  visible step) so the two indicators can never disagree about
   *  what "dirty" means for a tab. */
  private isTabDirty(tabId: string): boolean {
    return isTabDirty(
      tabId,
      this.sectionDirty.dirtyTab(),
      this.activePriceDirty(),
    );
  }

  /** Drives `SectionSaveBar`'s `[dirty]` input — true when the ACTIVE tab
   *  carries unsaved edits. */
  readonly sectionBarDirty = computed(() => this.isTabDirty(this.activeTab()));

  /** Short workflow label of the active tab — shown on the contextual
   *  SaveBar's button so the save's scope is explicit. */
  readonly activeTabShortLabel = computed(
    () =>
      computeTabItems(null).find((t) => t.id === this.activeTab())?.label ?? "",
  );

  /** Per-section Save — the per-section save cutover's main entry point.
   *  Fire-and-observe wrapper around `saveActiveSection` for callers (the
   *  save bar, Ctrl+S) that don't need to know whether it succeeded. */
  onSectionSave(): void {
    this.saveActiveSection(() => {
      // Fire-and-observe — this entry point doesn't need the outcome.
    });
  }

  /** Discard the active section's unsaved edits — restore the load/last-save
   *  snapshot and clear every dirty store. Shared by the contextual
   *  SaveBar's Discard button and `confirmUnsavedEdits()`'s Discard answer;
   *  safe as a whole-model restore because the single-dirty-scope invariant
   *  means only the active scope can differ from the snapshot. */
  onSectionDiscard(): void {
    this.discardSectionEdits();
  }

  private discardSectionEdits(): void {
    const snap = this.sectionDirty.snapshot();
    if (snap) this.enrichmentDataService.setEnrichment(snap);
    this.sectionDirty.clearTab();
    this.priceDirty.clear();
    this.priceLock.clear();
    this.isSofEdits.clear();
    this.dirty.markDiscarded();
  }

  /** Drives the SaveBar's save-to-stage mode: the Pricing tab is active and
   *  clean, and the active price is still inDesign/rejected — saving it
   *  as-is advances it to Designed (the BE owns the transition). */
  readonly sectionBarStageStatus = computed<string | null>(() => {
    if (this.activeTab() !== "price-config") return null;
    if (this.sectionBarDirty()) return null;
    const price = this.active.price();
    return price ? lifecycleActions(price).stageLabel : null;
  });

  /** Save-to-stage from the contextual bar: PATCH ONE price as-is so the BE
   *  advances its lifecycle (`inDesign`/`rejected` → `designed`). A clean
   *  price never lights the bar's dirty mode, so this is the only save path
   *  such a price has. Failure shows the plain incomplete modal; the bar
   *  stays in stage mode, so the user just presses the button again. */
  onStagePrice(): void {
    const priceId = this.active.popId();
    if (!priceId || !this.sectionBarStageStatus()) return;
    if (this.sectionSaving() || !this.canSave()) return;
    // Same confirm as a dirty price save. Staging IS a save — it PATCHes the
    // price so the BE advances it to Designed — and it is the one save whose
    // entire purpose is the status change, so going straight through without
    // saying what the status becomes was the odd one out.
    this.confirmPriceSave(
      [priceId],
      () => this.stagePrice(priceId),
      () => undefined,
    );
  }

  private stagePrice(priceId: string): void {
    if (this.sectionSaving() || this.refuseWrite("content")) return;
    this.sectionSaving.set(true);
    const onComplete = () => this.sectionSaving.set(false);
    const epochAtSend = this.sectionDirty.editEpoch();

    this.saver
      .savePrices(this.packageId, [priceId], onComplete)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result.ok) {
          this.toast.showSuccess("Price staged — Designed");
          if (this.sectionDirty.editEpoch() !== epochAtSend) {
            // Same mid-flight rule as runSectionSave: the coordinator kept
            // the newer keystrokes and left the price dirty; the persisted
            // state still advances the discard target.
            const snap = this.sectionDirty.snapshot();
            if (snap) {
              this.sectionDirty.setSnapshot(
                advanceSnapshot(snap, result.saved),
              );
            }
            return;
          }
          this.sectionDirty.clearTab();
          const model = this.enrichmentDataService.getEnrichment();
          if (model && isFlatEnrichmentModel(model)) {
            this.sectionDirty.setSnapshot(model);
          }
          if (!this.sectionDirty.isDirty()) this.dirty.markSaved();
          return;
        }
        logger.error("Error staging price:", result.raw);
        if (result.kind === "validation") {
          this.showSaveBlockedModal(this.saveErrorMessage(result.raw));
          return;
        }
        this.showSaveIncompleteModal(undefined);
      });
  }

  /** Same save as `onSectionSave`, but reports through `onDone(clean)`,
   *  called exactly once on every path: true when the active scope is clean
   *  afterwards, false when it is not (the save failed, the confirm was
   *  cancelled, or newer edits arrived while the PATCH was in flight). The
   *  unsaved-edits prompt's "Save & continue" leaves only on true.
   *  `planSectionSave` routes the ACTIVE tab's dirty scope to one request:
   *  a package tab sends the package PATCH, a price tab sends exactly the
   *  active price, and anything else no-ops (reporting clean) so a stale
   *  Retry (see `showSaveIncompleteModal`) can't fire an empty/wrong-scope
   *  save after the user has navigated away from the tab that failed.
   *  This is the one gate for every save entry (the bar, Ctrl+S, the
   *  prompt, Retry): a refused save says why and reports not clean. The
   *  scope and the language set are asked again in `runSectionSave`, as
   *  the request is built: the confirm can stay open across a role switch,
   *  or across the roles' arrival. */
  private saveActiveSection(onDone: (clean: boolean) => void): void {
    const plan = planSectionSave({
      tabId: this.activeTab(),
      activePopId: this.active.popId(),
      activePriceDirty: this.activePriceDirty(),
      dirtyTab: this.sectionDirty.dirtyTab(),
    });
    const refusal = plan.kind === "noop" ? null : this.saveRefusalMessage();
    if (refusal) {
      this.toast.showError(refusal, { duration: 4000 });
      onDone(false);
      return;
    }
    switch (plan.kind) {
      case "noop":
        onDone(true);
        return;
      case "price":
        // A price save is not neutral: the BE stages whatever it receives, so
        // the price lands on `designed` — and one that was on sale leaves the
        // app until it is published again. The Save button says none of that,
        // so ask first. Package saves skip the dialog: they never touch price
        // lifecycle, and a confirm claiming otherwise would be false.
        this.confirmPriceSave(
          plan.priceIds,
          (decision) =>
            this.runSectionSave(true, plan.priceIds, onDone, decision),
          () => onDone(false),
        );
        return;
      case "package":
        this.runSectionSave(false, [], onDone);
    }
  }

  /** Ask before a price save, in the words of what is actually being saved —
   *  {@link buildSaveConfirmCopy} keeps the wording honest about whether
   *  anything in this batch is really on sale. When something is on sale and
   *  the user holds `enrichment:publish`, the dialog instead asks whether to
   *  keep it on sale (default) or take it off sale; everyone else gets the
   *  plain confirm and the save stages as before. Exactly one of `proceed`
   *  and `cancel` is called; ✕/Escape and Cancel both cancel, so a caller
   *  waiting on the outcome (the unsaved-edits prompt) always hears back. */
  private confirmPriceSave(
    priceIds: readonly string[],
    proceed: (decision: PriceSaveDecision) => void,
    cancel: () => void,
  ): void {
    const wanted = new Set(priceIds);
    const prices = this.flatPrices().filter((p) => wanted.has(p.id ?? ""));
    const copy = buildSaveConfirmCopy(prices, {
      mayKeepLive: this.mayKeepLiveOnSave(),
    });
    const config = {
      title: copy.title,
      primaryButtonText: copy.primaryButtonText,
      secondaryButtonText: "Cancel",
      primaryButtonVariant: "primary" as const,
      secondaryButtonVariant: "text" as const,
      showCloseButton: false,
      backdropDismiss: false,
    };

    if (copy.mode === "plain") {
      answerOnce(
        this.omniarkModal.showModal({ ...config, message: copy.message }),
        undefined,
      )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {
          if (result === "primary") proceed({ keepLifecycleStatus: false });
          else cancel();
        });
      return;
    }

    this.priceSaveLiveChoice.set("keep");
    this.priceSaveConfirmCopy.set(copy);
    answerOnce(
      this.omniarkModal.showTemplateModal(this.priceSaveConfirmTemplate, {
        ...config,
        description: copy.message,
      }),
      undefined,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === "primary") {
          proceed({
            keepLifecycleStatus: this.priceSaveLiveChoice() === "keep",
          });
        } else {
          cancel();
        }
      });
  }

  private runSectionSave(
    isPriceTab: boolean,
    priceIds: readonly string[],
    onDone: (ok: boolean) => void,
    decision: PriceSaveDecision = DEFAULT_PRICE_SAVE_DECISION,
  ): void {
    if (this.refuseWrite("content")) {
      onDone(false);
      return;
    }
    this.sectionSaving.set(true);
    const onComplete = () => this.sectionSaving.set(false);
    // The save is non-blocking, so the user can keep typing while the PATCH
    // is in flight — those edits are NOT in the body captured here. Capture
    // the edit epoch now; if it moved by completion, the success handler
    // must not clear/snapshot the newer edits as if they were saved.
    const epochAtSend = this.sectionDirty.editEpoch();
    // How many of the sent prices are on sale — read now, before the save
    // replaces the model with the response. Only counts when the choice was
    // offered (the same `mayKeepLiveOnSave()` gate as the dialog), so a save
    // without the choice keeps the plain "Saved" toast.
    const sent = new Set(priceIds);
    const liveCount = this.mayKeepLiveOnSave()
      ? this.flatPrices().filter(
          (p) => sent.has(p.id ?? "") && isRejectableStatus(p.lifecycleStatus),
        ).length
      : 0;

    const save$ = isPriceTab
      ? this.saver.savePrices(this.packageId, priceIds, onComplete, decision)
      : this.saver.savePackageSection(this.packageId, onComplete);

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result.ok) {
        if (this.sectionDirty.editEpoch() !== epochAtSend) {
          // Edits arrived mid-flight. The sent body IS persisted, and the
          // coordinator committed the server's copy only where nothing
          // moved: the newer keystrokes are still in the model and their
          // scope is still dirty. So skip clearTab/markSaved and report
          // the scope as not clean, which keeps a prompt that started this
          // save on the price. The persisted state still advances the
          // discard snapshot (from the result, never from the model, which
          // now holds unsaved text) so a later Discard never restores
          // pre-save values.
          this.toast.showSuccess("Saved. Newer edits are still unsaved.");
          const snap = this.sectionDirty.snapshot();
          if (snap) {
            this.sectionDirty.setSnapshot(advanceSnapshot(snap, result.saved));
          }
          onDone(false);
          return;
        }
        this.toast.showSuccess(saveSuccessToast(liveCount, decision));
        this.sectionDirty.clearTab();
        const model = this.enrichmentDataService.getEnrichment();
        if (model && isFlatEnrichmentModel(model)) {
          this.sectionDirty.setSnapshot(model);
        }
        if (!this.sectionDirty.isDirty()) this.dirty.markSaved();
        onDone(true);
        return;
      }

      logger.error("Error saving enrichment section:", result.raw);
      if (result.kind === "validation") {
        this.showSaveBlockedModal(this.saveErrorMessage(result.raw));
        onDone(false);
        return;
      }
      if (
        result.kind === "partial" &&
        decision.keepLifecycleStatus &&
        // Only when nothing saved: a batch with saved prices must still go
        // through the partial branch below, which advances their snapshot.
        !result.savedPrices.length &&
        result.failed.every(
          (f) => f.raw instanceof HttpErrorResponse && f.raw.status === 403,
        )
      ) {
        // The permission to keep a price on sale was lost mid-session: the BE
        // wrote nothing. Retry would send the same flag and 403 again, so show
        // the reason with a single OK; the edits stay dirty.
        this.showSaveBlockedModal(
          "Your edits were not saved. Keeping a price on sale while saving " +
            "needs publish permission, which you no longer have. Save again " +
            "and choose Take off sale, or contact your administrator for " +
            "publish permission.",
        );
        onDone(false);
        return;
      }
      if (result.kind === "partial") {
        // The saved half of the fan-out is persisted and committed to the
        // model; its dirty ids are already cleared. Advance the snapshot by
        // EXACTLY those server copies so a later guard Discard can't revert
        // persisted prices while the failed prices' unsaved edits stay out
        // of the discard target.
        const snap = this.sectionDirty.snapshot();
        if (snap) {
          this.sectionDirty.setSnapshot(
            advanceSnapshot(snap, {
              kind: "prices",
              prices: result.savedPrices,
            }),
          );
        }
      }
      this.showSaveIncompleteModal(
        result.kind === "partial"
          ? "This price failed to save. Press Retry to send it again."
          : undefined,
      );
      onDone(false);
    });
  }

  /** Publish action — a dedicated status-only call
   *  (`PATCH /enrichment/publish`) that advances this channel's overlay from
   *  `designed` to `active`. Refreshes the model from the response so the hero
   *  + stepper reflect the new status, and celebrates on success. */
  private performPublish(priceIds: NonEmptyPriceIds): void {
    if (this.refuseWrite("status")) return;
    this.overlayService.showLoading("Publishing price...");
    this.dirty.markSaving();

    this.saver
      .performPublish(this.packageId, priceIds, () =>
        this.overlayService.hideLoading(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result.ok) {
          // The PO badge stays as the CPC sent it — publish transitions the
          // selected PRICES, which the coordinator already committed to the
          // model, and the per-price gates (canReject/hasPublishablePrice)
          // recompute from there.
          this.dirty.markSaved();
          // The editor stays on the price, so the discard target must carry
          // the published status: a Discard that restored the pre-publish
          // model would read a live price as Designed, and the next save
          // would take it off sale without asking. Publish runs only on a
          // clean editor, so the whole committed model is the safe target.
          const model = this.enrichmentDataService.getEnrichment();
          if (model && isFlatEnrichmentModel(model)) {
            this.sectionDirty.setSnapshot(model);
          }
          this.celebratePublishIfNeeded(false);
          this.showPublishedModal();
          return;
        }

        logger.error("Error publishing enrichment price:", result.raw);
        this.dirty.markSaveCancelled();
        this.showPublishFailedModal(
          this.backendErrorMessage(result.raw),
          priceIds,
        );
      });
  }

  /** Pull the human-readable reason out of an HTTP error. The Go enricher
   *  envelope carries it as `error.desc` (older services use `resultDesc`);
   *  Publish returns a meaningful one (e.g. "already active", "no overlay to
   *  publish"), so we surface it verbatim instead of a generic message. */
  private backendErrorMessage(raw: unknown): string {
    const err = raw as {
      error?: { desc?: string; resultDesc?: string; message?: string };
      message?: string;
    } | null;
    return (
      err?.error?.desc ||
      err?.error?.resultDesc ||
      err?.error?.message ||
      ""
    ).trim();
  }

  private showPublishFailedModal(
    backendMessage: string,
    priceIds: NonEmptyPriceIds,
  ): void {
    this.omniarkModal
      .showModal({
        title: "Publish failed",
        message: backendMessage || "เผยแพร่ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
        primaryButtonText: "ลองใหม่ (Retry)",
        secondaryButtonText: "ยกเลิก (Cancel)",
        primaryButtonVariant: "primary",
        secondaryButtonVariant: "text",
        showCloseButton: false,
        backdropDismiss: false,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === "primary") this.performPublish(priceIds);
      });
  }

  /** Phase 4.4 — fires a lightweight confetti burst on successful publish.
   *  Skips for drafts and respects prefers-reduced-motion so the delight
   *  isn't an accessibility hazard. */
  private celebratePublishIfNeeded(isDraft: boolean): void {
    if (isDraft) return;
    if (typeof window === "undefined" || typeof document === "undefined")
      return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    triggerPublishConfetti();
  }

  /** A save was blocked client-side before the PATCH (e.g. an invalid price
   *  discount). Retrying the identical body can't help, so we show the exact
   *  reason with a single acknowledge button and point the user at the field
   *  to correct — no "Retry" that would just re-trip the same guard. */
  private showSaveBlockedModal(reason: string): void {
    this.omniarkModal
      .showModal({
        title: "Cannot save (ยังบันทึกไม่ได้)",
        message: reason || "ข้อมูลบางส่วนไม่ถูกต้อง กรุณาแก้ไขก่อนบันทึก",
        primaryButtonText: "ตกลง (OK)",
        primaryButtonVariant: "primary",
        showCloseButton: true,
        backdropDismiss: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  /** Pull a human-readable reason out of a save error. Client-side validation
   *  blocks carry a plain `Error` (its `message`); backend failures carry the
   *  Go envelope, so fall back to {@link backendErrorMessage}. */
  private saveErrorMessage(raw: unknown): string {
    if (raw instanceof Error && raw.message.trim()) return raw.message.trim();
    return this.backendErrorMessage(raw);
  }

  /** "Some/all of the save failed" modal for a per-section save failure. Its
   *  Retry re-runs `onSectionSave()` (the per-section save cutover's single
   *  entry point), which re-derives what to send from the CURRENT active tab
   *  + dirty stores — so it naturally covers a stale failure as long as the
   *  user is still on the tab that failed. */
  private showSaveIncompleteModal(message?: string): void {
    this.omniarkModal
      .showModal({
        title: "Save incomplete",
        message: message ?? "ข้อมูลบางส่วนบันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
        primaryButtonText: "ลองใหม่ (Retry)",
        secondaryButtonText: "ยกเลิก (Cancel)",
        primaryButtonVariant: "primary",
        secondaryButtonVariant: "text",
        showCloseButton: false,
        backdropDismiss: false,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === "primary") this.onSectionSave();
      });
  }
}

export type { PublishReadiness };
